import React, { useEffect, useState } from "react";
import { RefreshCw, Plus } from "lucide-react";
import { adminAPI } from "../../services/api/admin";
import { useToast } from "../../contexts/ToastContext";
import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import FormField from "../../components/ui/FormField";
import Pagination from "../../components/ui/Pagination";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../../components/ui/table";

const PAGE_SIZE = 25;
const inputClass =
  "w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary";

export default function AdminPhoneNumbers() {
  const { showToast } = useToast();
  const [numbers, setNumbers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [page, setPage] = useState(1);

  const emptyAddForm = { e164_number: "", sid: "", friendly_name: "", provider: "twilio" };
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [addError, setAddError] = useState(null);
  const [assignClientId, setAssignClientId] = useState("");
  const [assignError, setAssignError] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await adminAPI.getPhoneNumbers();
      setNumbers(res.phone_numbers || []);
      setWorkspaces(res.workspaces || []);
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        setLoadError("Phone-number management is not yet configured on this server. The backend endpoint /admin/phone_numbers is not available.");
      } else {
        const msg = e.response?.data?.error || e.message || "Failed to load phone numbers";
        setLoadError(msg);
        showToast(msg, "error");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const doFetch = async () => {
    setFetching(true);
    try {
      const res = await adminAPI.fetchPhoneNumbersFromTwilio();
      showToast(`Fetched from Twilio — ${res.inserted} new, ${res.updated} updated, ${res.total} total`, "success");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Failed to fetch from Twilio", "error");
    } finally {
      setFetching(false);
    }
  };

  const doAdd = async (e) => {
    if (e) e.preventDefault();
    setAddError(null);
    const values = { ...addForm };
    if (!values.e164_number) {
      setAddError("Required");
      return;
    }
    if (!/^\+[1-9]\d{6,14}$/.test(values.e164_number)) {
      setAddError("Must be E.164 (e.g. +14155551234)");
      return;
    }
    // Plivo/Vobiz numbers have no Twilio SID — use the (already-unique) E.164
    // number as the stored identifier.
    if (values.provider !== "twilio" && !values.sid) {
      values.sid = values.e164_number;
    }
    if (!values.sid) {
      setAddError("Required");
      return;
    }
    try {
      await adminAPI.createPhoneNumber(values);
      showToast("Phone number added", "success");
      setAddOpen(false);
      setAddForm(emptyAddForm);
      load();
    } catch (e) {
      const err = e.response?.data?.error;
      showToast(Array.isArray(err) ? err.join(", ") : err || "Add failed", "error");
    }
  };

  const doAssign = async (e) => {
    if (e) e.preventDefault();
    setAssignError(null);
    if (!assignClientId) {
      setAssignError("Pick a workspace");
      return;
    }
    try {
      await adminAPI.assignPhoneNumber(assignOpen.id, assignClientId);
      showToast("Assigned", "success");
      setAssignOpen(null);
      setAssignClientId("");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Assign failed", "error");
    }
  };

  const doUnassign = async (row) => {
    try {
      await adminAPI.unassignPhoneNumber(row.id);
      showToast("Unassigned", "success");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Unassign failed", "error");
    }
  };

  const doSetDefault = async (row) => {
    try {
      await adminAPI.setPhoneNumberAsDefault(row.id);
      showToast("Set as workspace default", "success");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Set default failed", "error");
    }
  };

  const doSetCarrierDefault = async (row) => {
    try {
      await adminAPI.setPhoneNumberAsCarrierDefault(row.id);
      showToast(`Set as default ${row.provider} number`, "success");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Set carrier default failed", "error");
    }
  };

  const doUnsetCarrierDefault = async (row) => {
    try {
      await adminAPI.unsetPhoneNumberAsCarrierDefault(row.id);
      showToast(`Removed default ${row.provider} number`, "success");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Unset failed", "error");
    }
  };

  const doDelete = async (row) => {
    try {
      await adminAPI.deletePhoneNumber(row.id);
      showToast("Deleted", "success");
      load();
    } catch (e) {
      showToast(e.response?.data?.error || "Delete failed", "error");
    }
  };

  const columns = [
    {
      title: "Number",
      key: "e164_number",
      render: (row) => (
        <div>
          <div className="font-medium text-foreground">{row.e164_number}</div>
          {row.friendly_name && <div className="text-xs text-muted-foreground">{row.friendly_name}</div>}
        </div>
      ),
    },
    {
      title: "Provider / SID",
      key: "provider",
      render: (row) => (
        <div>
          <Badge variant="info">{row.provider}</Badge>
          <div className="text-xs text-muted-foreground font-mono mt-1">{row.sid}</div>
        </div>
      ),
    },
    {
      title: "Workspace",
      key: "client",
      render: (row) =>
        row.is_global_default ? (
          <Badge variant="info">Global default (all workspaces)</Badge>
        ) : row.client ? (
          <div className="flex flex-col gap-0">
            <span className="text-foreground">{row.client.name}</span>
            {row.is_default_for_workspace && <Badge variant="success">Default</Badge>}
            {row.is_carrier_default && <Badge variant="info">Default {row.provider}</Badge>}
          </div>
        ) : row.is_carrier_default ? (
          <Badge variant="info">Carrier default ({row.provider})</Badge>
        ) : (
          <Badge variant="muted">Unassigned</Badge>
        ),
    },
    {
      title: "Agent Overrides",
      key: "agent_override_count",
      render: (row) =>
        row.agent_override_count > 0 ? (
          <Badge variant="muted">{row.agent_override_count} agent(s)</Badge>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (row) =>
        row.is_global_default ? (
          <span className="text-xs text-muted-foreground">Shared — no per-workspace actions</span>
        ) : (
        <div className="flex items-center gap-2">
          {row.client ? (
            <>
              {!row.is_default_for_workspace && (
                <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => doSetDefault(row)}>
                  Set as default
                </Button>
              )}
              {!row.is_carrier_default && (
                <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => doSetCarrierDefault(row)}>
                  Default {row.provider}
                </Button>
              )}
              <Button
                variant="danger"
                className="px-2 py-1 text-xs"
                onClick={() => {
                  if (window.confirm("Unassign from workspace? Agent overrides will be cleared.")) {
                    doUnassign(row);
                  }
                }}
              >
                Unassign
              </Button>
            </>
          ) : row.is_carrier_default ? (
            <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => doUnsetCarrierDefault(row)}>
              Unset default
            </Button>
          ) : (
            <>
              <Button
                variant="primary"
                className="px-2 py-1 text-xs"
                onClick={() => {
                  setAssignOpen(row);
                  setAssignClientId("");
                  setAssignError(null);
                }}
              >
                Assign
              </Button>
              <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => doSetCarrierDefault(row)}>
                Default {row.provider}
              </Button>
            </>
          )}
          <Button
            variant="danger"
            className="px-2 py-1 text-xs"
            disabled={row.is_default_for_workspace}
            onClick={() => {
              if (window.confirm("Delete from local pool? (does NOT release on Twilio)")) {
                doDelete(row);
              }
            }}
          >
            Delete
          </Button>
        </div>
        ),
    },
  ];

  const totalPages = Math.ceil(numbers.length / PAGE_SIZE) || 1;
  const pageRows = numbers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Phone Numbers</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Phone numbers (Twilio, Plivo, Vobiz) available for outbound calls. Workspaces get a
              number auto-assigned on creation from the unassigned pool; admins can reassign, set a
              per-carrier default, or set an override per agent.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={doFetch} disabled={fetching}>
              <RefreshCw size={14} />
              <span>Fetch from Twilio</span>
            </Button>
            <Button variant="primary" onClick={() => setAddOpen(true)}>
              <Plus size={14} />
              <span>Add manually</span>
            </Button>
          </div>
        </div>
      </Card>

      {loadError ? (
        <Card className="p-4">
          <div className="text-center py-12">
            <p className="text-sm font-medium text-foreground mb-1">Phone numbers unavailable</p>
            <p className="text-sm text-muted-foreground">{loadError}</p>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHead key={col.key}>{col.title}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-8">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center text-muted-foreground py-8">
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((col) => (
                      <TableCell key={col.key}>{col.render(row)}</TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <div className="px-4 pb-3">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </Card>
      )}

      {/* Add manual modal */}
      {addOpen && (
        <Modal
          title="Add phone number manually"
          onClose={() => setAddOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => doAdd()}>
                Add
              </Button>
            </>
          }
        >
          <form onSubmit={doAdd} className="space-y-4">
            <FormField label="E.164 number" required hint={addError || undefined}>
              <input
                className={inputClass}
                placeholder="+14155551234"
                value={addForm.e164_number}
                onChange={(e) => setAddForm({ ...addForm, e164_number: e.target.value })}
              />
            </FormField>
            <FormField label="Provider">
              <select
                className={inputClass}
                value={addForm.provider}
                onChange={(e) => setAddForm({ ...addForm, provider: e.target.value })}
              >
                <option value="twilio">Twilio</option>
                <option value="plivo">Plivo</option>
                <option value="vobiz">Vobiz</option>
                <option value="telnyx">Telnyx (Beta)</option>
                <option value="exotel">Exotel (Beta)</option>
              </select>
            </FormField>
            {addForm.provider === "twilio" && (
              <FormField label="Twilio SID" required>
                <input
                  className={inputClass}
                  placeholder="PN…"
                  value={addForm.sid}
                  onChange={(e) => setAddForm({ ...addForm, sid: e.target.value })}
                />
              </FormField>
            )}
            <FormField label="Friendly name (optional)">
              <input
                className={inputClass}
                placeholder="SF office line"
                value={addForm.friendly_name}
                onChange={(e) => setAddForm({ ...addForm, friendly_name: e.target.value })}
              />
            </FormField>
            <button type="submit" className="hidden" />
          </form>
        </Modal>
      )}

      {/* Assign modal */}
      {assignOpen && (
        <Modal
          title={`Assign ${assignOpen.e164_number}`}
          onClose={() => setAssignOpen(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setAssignOpen(null)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => doAssign()}>
                Assign
              </Button>
            </>
          }
        >
          <form onSubmit={doAssign} className="space-y-4">
            <FormField label="Workspace" required hint={assignError || undefined}>
              <select
                className={inputClass}
                value={assignClientId}
                onChange={(e) => setAssignClientId(e.target.value)}
              >
                <option value="">Select workspace</option>
                {workspaces.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </FormField>
            <p className="text-xs text-muted-foreground">
              If the workspace has no default yet, this number will be set as its default automatically.
            </p>
            <button type="submit" className="hidden" />
          </form>
        </Modal>
      )}
    </div>
  );
}
